import { useAuthenticatedQuery } from '../useAuthenticatedQuery';
import axios from 'axios';
import { API_BASE_URL, FUNCTION_KEY, PROJECT_SCOPE } from '../../config';
import { convertDateToLocal } from '../metadata/DataUtils';
import logError from '../metadata/ErrorLogging';
import PropTypes from 'prop-types';

/** @typedef {import("@components/cards/events/events").TestEvent} TestEvent */

/**
 * Fetches the list of test events.
 * 
 * @function useEvents
 * @param {string} event_level - The level of the event (e.g., 'Test', 'Project', 'Release').
 * @param {string} project_id - The ID of the project.
 * @param {boolean} [live_only=true] - Whether to fetch only live events - start before now and end null or after now.
 * @param {string|null} [release_id=null] - The ID of the release (only for 'Test' and 'Release' level events).
 * @param {string|null} [test_type_id=null] - The ID of the test type (only for 'Test' level events).
 * 
* @returns {{
*   events: TestEvent[] | [],
*   isLoading: boolean,
*   refetch: () => Promise<any>,
*   error: any
* }}
 */
export default function useEvents(event_level, project_id, live_only = true, release_id = null, test_type_id = null) {

  const fetchEvents = async (token) => {

    const theParams = {
        event_level: event_level,
        project_id: project_id,
        live_only: live_only,
        release_id: release_id,
        test_type_id: test_type_id
    };
    
    try {
        const response = await axios.get(`${API_BASE_URL}events`, {
            headers: {
            'Authorization': `Bearer ${token}`,
            'x-functions-key': FUNCTION_KEY
            },
            params: theParams
        });

        // Sort events by severity then start_time
        const localTimeEvents = response.data.map(event => {
            // Convert the dates from UTC to user's local format
            if (event.start_time) {
              event.formatted_start_time = convertDateToLocal(event.start_time, true, true, true);
            }

            if (event.end_time) {
                event.formatted_end_time = convertDateToLocal(event.end_time, true, true, true);
            }
        
            return event;
        });

        return localTimeEvents;
    } catch (error) {
      logError('Failed to load events in useEvents', error, theParams); // Error during fetch
      throw error;
    }
  };

  const projectScope = [PROJECT_SCOPE];
  const queryKey = ['events', event_level, project_id, release_id, test_type_id, live_only];

  const { data: events = [], isLoading, refetch, error } = useAuthenticatedQuery(queryKey, fetchEvents, projectScope, {
    enabled: !!project_id,
    staleTime: 30 * 60 * 1000,  // Data will be cached for 30 minutes
  });

  return { events, isLoading, refetch, error };
}

useEvents.propTypes = {
  event_level: PropTypes.string.isRequired,
  project_id: PropTypes.string,
  release_id: PropTypes.string,
  test_type_id: PropTypes.string,
  live_only: PropTypes.bool,
};